"use client";
import { motion } from "framer-motion";
import { Package, ArrowLeft, Zap, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

export default function ShippingPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto px-4 py-8"
      >
        <div className="flex items-center justify-between mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6"
          >
            <Package className="h-8 w-8 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            Shipping & Delivery Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Digital service delivery information
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-white/[0.1] space-y-8">
            
            {/* Digital Service */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Cloud className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Digital Service - No Physical Shipping</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  AssignOwl is a <strong className="text-foreground">100% digital, cloud-based service</strong>. We do not ship any physical products. All services, features, and deliverables are provided electronically through our web platform.
                </p>
              </div>
            </section>

            {/* Instant Access */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Instant Service Delivery</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  Upon successful registration and credit purchase, you receive:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-foreground">Immediate Access:</strong> Your account is activated instantly upon email verification</li>
                  <li><strong className="text-foreground">Instant Credits:</strong> Purchased credits are added to your account immediately after payment confirmation</li>
                  <li><strong className="text-foreground">Fast Grading:</strong> Assignment grading results are typically delivered within minutes</li>
                  <li><strong className="text-foreground">Real-time Reports:</strong> Download your detailed reports instantly from your dashboard</li>
                  <li><strong className="text-foreground">24/7 Availability:</strong> Access your account, credits, and history anytime, from anywhere</li>
                </ul>
              </div>
            </section>

            {/* How It Works */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">How Service Delivery Works</h2>
              <div className="space-y-4 text-muted-foreground">
                <ol className="space-y-3 list-decimal list-inside">
                  <li>
                    <strong className="text-foreground">Upload Assignment:</strong> Submit your assignment document along with rubric criteria through our web interface
                  </li>
                  <li>
                    <strong className="text-foreground">AI Processing:</strong> Our AI system analyzes and scores your assignment (typically takes 10-60 seconds depending on length)
                  </li>
                  <li>
                    <strong className="text-foreground">Instant Results:</strong> View detailed scores, feedback, and rubric breakdown immediately on your dashboard
                  </li>
                  <li>
                    <strong className="text-foreground">Download Reports:</strong> Export your scoring reports as PDF or other formats instantly
                  </li>
                </ol>
              </div>
            </section>

            {/* Delivery Timeframes */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Service Processing Times</h2>
              <div className="space-y-4 text-muted-foreground">
                <div className="p-4 rounded-lg bg-muted/10 border border-white/[0.05]">
                  <p className="font-medium text-foreground mb-2">Standard Processing:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Short assignments (1-5 pages): 10-30 seconds</li>
                    <li>Medium assignments (6-15 pages): 30-60 seconds</li>
                    <li>Long assignments (16+ pages): 1-3 minutes</li>
                  </ul>
                </div>
                <p className="text-sm">
                  <strong className="text-foreground">Note:</strong> Processing times may vary during peak usage hours. Our system automatically queues requests and processes them in the order received.
                </p>
              </div>
            </section>

            {/* Account Access */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Account Access & Availability</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Global Access:</strong> AssignOwl is accessible worldwide through any modern web browser with an internet connection.
                </p>
                <p>
                  <strong className="text-foreground">System Requirements:</strong>
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                  <li>Stable internet connection</li>
                  <li>JavaScript enabled</li>
                  <li>Cookies enabled for authentication</li>
                </ul>
              </div>
            </section>

            {/* Data Delivery */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Report & Data Delivery</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>All reports and data are delivered digitally:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-foreground">In-Platform Viewing:</strong> Access all scores and feedback directly in your browser</li>
                  <li><strong className="text-foreground">Downloadable Reports:</strong> Export reports in PDF format instantly</li>
                  <li><strong className="text-foreground">Email Notifications:</strong> Receive email alerts when scoring is complete (if enabled)</li>
                  <li><strong className="text-foreground">History Access:</strong> All past assignments and scores remain accessible in your account</li>
                </ul>
              </div>
            </section>

            {/* Service Interruptions */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Service Interruptions</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  In rare cases of service interruptions or technical issues:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Your submission will be queued and processed once service resumes</li>
                  <li>You will be notified via email when your results are ready</li>
                  <li>No assignments are lost - all uploads are securely stored</li>
                  <li>Check our status page for real-time service health information</li>
                </ul>
              </div>
            </section>

            {/* No Physical Products */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">No Physical Products or Shipping</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Since AssignOwl is entirely digital:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>No shipping addresses are required</li>
                  <li>No shipping fees apply</li>
                  <li>No delivery delays due to logistics</li>
                  <li>No customs or international shipping concerns</li>
                  <li>Zero environmental impact from physical shipping</li>
                </ul>
              </div>
            </section>

            {/* Support */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Delivery Issues & Support</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  If you experience issues accessing our service or receiving your results:
                </p>
                <ol className="space-y-2 list-decimal list-inside">
                  <li>Check your internet connection</li>
                  <li>Verify your email address is confirmed</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Try accessing from a different browser or device</li>
                  <li>Contact our support team at <strong className="text-foreground">support@assignowl.com</strong></li>
                </ol>
              </div>
            </section>

            {/* Contact */}
            <section className="pt-6 border-t border-white/[0.1]">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Need Help?</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>If you have questions about service delivery or access, please contact us:</p>
                <p className="text-foreground">
                  <strong>Email:</strong> support@assignowl.com<br />
                  <strong>Company:</strong> Greenguide Solutions Pvt Limited
                </p>
              </div>
            </section>

            <div className="text-sm text-muted-foreground text-center pt-6 border-t border-white/[0.1]">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
